import React from 'react';
import { IonItem, IonLabel } from '@ionic/react';
import { useSmartCase } from '@/modules/footer';
import SectionCard from '@/components/SectionCard';
import { TopicLogic } from '../logic';
import {
  getBrowserTimezone,
  normalizeTimezone,
  formatTimezoneLabel,
} from '../timezone';
import type { Topic } from '../types';
import { fieldLabelStyle, fieldValueStyle } from './styles';

const formatTopicDateTimeLinesSafe = (topic: Topic, tz: string): string[] => {
  try {
    return TopicLogic.formatTopicDateTimeLines(topic, normalizeTimezone(tz));
  } catch {
    return [];
  }
};

const tzAbbrev = (tz: string): string | null => {
  const match = formatTimezoneLabel(tz).match(/\(([^)]+)\)/);
  return match ? match[1].toUpperCase() : null;
};

/**
 * View-mode "Where & when" card for the date-anchored topic types: the
 * topic's location, when it has one, above the authored start/end lines in
 * the topic's timezone, plus the same lines in the viewer's local timezone
 * when it differs. A topic with no location gets the plain When card, which
 * the authored timezone titles, so only the local-time rows need an in-card
 * label. In the Where & when card the authored rows are
 * labelled with their timezone too. Renders nothing when the topic has
 * neither a location nor a formattable date/time.
 */
interface WhereWhenBlockProps {
  topic: Topic;
  /** The location's label, in sentence case; "Location" unless given. */
  locationLabel?: string;
}

const WhereWhenBlock: React.FC<WhereWhenBlockProps> = ({
  topic,
  locationLabel = 'Location',
}) => {
  const smartCase = useSmartCase();
  const browserTz = getBrowserTimezone();
  const normalizedTopicTimezone = normalizeTimezone(topic.timezone);
  const authoredLines = formatTopicDateTimeLinesSafe(topic, topic.timezone);
  const showLocalTime = browserTz !== normalizedTopicTimezone;
  const localLines = showLocalTime
    ? formatTopicDateTimeLinesSafe(topic, browserTz)
    : [];
  const location = topic.location?.trim();

  const locationRow = location && (
    <IonItem lines={authoredLines.length > 0 ? 'inset' : 'none'}>
      <IonLabel>
        <p style={fieldLabelStyle}>{smartCase(locationLabel)}</p>
        <h2 data-testid="topic-location" style={fieldValueStyle}>
          {location}
        </h2>
      </IonLabel>
    </IonItem>
  );

  if (authoredLines.length === 0) {
    return locationRow ? (
      <SectionCard title="Where & when">{locationRow}</SectionCard>
    ) : null;
  }

  // The card's title, which the standard section-label style uppercases — so
  // there is no per-mode casing to match here, unlike the in-card labels below.
  const authoredAbbr = tzAbbrev(normalizedTopicTimezone);
  const whenLabel = authoredAbbr ? `When (${authoredAbbr} timezone)` : 'When';
  // The in-card labels, written in sentence case and cased for the Ionic mode
  // like the app's other labels: title case in ios mode, as written in md.
  const tzLabel = (base: string, abbr: string | null) =>
    smartCase(abbr ? `${base} (${abbr} timezone)` : base);
  const authoredTzLabel = tzLabel('Time', authoredAbbr);
  const localTzLabel = tzLabel('Local time', tzAbbrev(browserTz));

  return (
    <SectionCard title={location ? 'Where & when' : whenLabel}>
      {locationRow}
      <IonItem lines={showLocalTime ? 'inset' : 'none'}>
        <IonLabel>
          {location && <p style={fieldLabelStyle}>{authoredTzLabel}</p>}
          {authoredLines.map((line, i) => (
            <h2
              key={i}
              data-testid="topic-datetime-line"
              style={fieldValueStyle}
            >
              {line}
            </h2>
          ))}
        </IonLabel>
      </IonItem>
      {showLocalTime && (
        <IonItem lines="none">
          <IonLabel>
            <p style={fieldLabelStyle}>{localTzLabel}</p>
            {localLines.map((line, i) => (
              <h2
                key={i}
                data-testid="topic-local-datetime-line"
                style={fieldValueStyle}
              >
                {line}
              </h2>
            ))}
          </IonLabel>
        </IonItem>
      )}
    </SectionCard>
  );
};

export default WhereWhenBlock;
